import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    FaPills,
    FaSyringe,
    FaBandAid,
    FaHeartbeat,
    FaArrowRight,
    FaCheck,
    FaHeart,
    FaSearch
} from 'react-icons/fa';
import './Products.css';

const productsData = [
    {
        id: 'medicines',
        title: 'Medicines',
        description: 'Comprehensive range of pharmaceutical products from certified manufacturers with complete documentation and batch verification.',
        icon: FaPills,
        color: '#A41E1E',
        features: [
            'Tablets & Capsules',
            'Injectable Solutions',
            'Syrups & Suspensions',
            'Ointments & Creams',
            'Certified Manufacturers'
        ]
    },
    {
        id: 'surgical',
        title: 'Surgical Items',
        description: 'High-quality surgical instruments and operation theatre supplies for hospitals and clinics, sourced from trusted manufacturers.',
        icon: FaSyringe,
        color: '#2E8B57',
        features: [
            'Cutting & Grasping Instruments',
            'Hemostatic & Suturing Tools',
            'Retractors & Electrosurgical',
            'Specialized OT Equipment',
            'Surgical Consumables'
        ],
        link: '/products/surgical'
    },
    {
        id: 'consumables',
        title: 'Medical Consumables',
        description: 'Essential daily-use healthcare consumables for hospitals, clinics, and nursing homes with consistent quality and supply.',
        icon: FaBandAid,
        color: '#1A1A1A',
        features: [
            'Syringes & Needles',
            'IV Sets & Catheters',
            'Bandages & Dressings',
            'Cotton & Gauze',
            'Disposable Items'
        ]
    },
    {
        id: 'supplies',
        title: 'Healthcare Supplies',
        description: 'General healthcare products and institutional care supplies to support comprehensive patient care and facility operations.',
        icon: FaHeartbeat,
        color: '#A41E1E',
        features: [
            'Sanitizers & Disinfectants',
            'Thermometers',
            'BP Monitors',
            'Diagnostic Supplies',
            'Institutional Care Items'
        ]
    },
    {
        id: 'cardiac',
        title: 'Cardiac Products',
        description: 'Complete range of cardiac catheterization and intervention supplies including catheters, balloons, sheaths, guide wires, and accessories.',
        icon: FaHeart,
        color: '#C41E3A',
        features: [
            'Guiding & Diagnostic Catheters',
            'PTCA Balloons (SC & NC)',
            'Sheaths & Guide Wires',
            'Drape Sheets & Trays',
            'Cardiac Accessories'
        ],
        link: '/products/cardiac'
    }
];

function Products() {
    const [searchQuery, setSearchQuery] = useState('');

    const filteredProducts = productsData.filter((product) => {
        const query = searchQuery.toLowerCase();
        return (
            product.title.toLowerCase().includes(query) ||
            product.description.toLowerCase().includes(query) ||
            product.features.some(f => f.toLowerCase().includes(query))
        );
    });

    return (
        <div className="products-page">
            {/* Page Header */}
            <section className="page-header">
                <div className="container">
                    <h1>Our Product Categories</h1>
                    <p>Comprehensive range of medical products for healthcare institutions</p>
                </div>
            </section>

            {/* Search Bar */}
            <section className="products-search-section">
                <div className="container">
                    <div className="products-search-bar">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search products... (e.g., Medicines, Surgical, Cardiac, Syringes)"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            </section>

            {/* Products Section */}
            <section className="section products-list">
                <div className="container">
                    <div className="products-intro">
                        <p>
                            We supply a wide range of medical products to hospitals, clinics, nursing homes,
                            and healthcare institutions. All products are sourced from certified manufacturers
                            and verified suppliers with complete documentation.
                        </p>
                    </div>

                    {filteredProducts.length === 0 ? (
                        <div className="no-results">
                            <p>No product categories found matching "{searchQuery}"</p>
                        </div>
                    ) : (
                    <div className="products-grid">
                        {filteredProducts.map((product) => {
                            const IconComponent = product.icon;
                            return (
                                <div key={product.id} className="product-card">
                                    <div className="product-header">
                                        <div
                                            className="product-icon"
                                            style={{ backgroundColor: `${product.color}15`, color: product.color }}
                                        >
                                            <IconComponent />
                                        </div>
                                        <h2>{product.title}</h2>
                                    </div>
                                    <p className="product-description">{product.description}</p>
                                    <ul className="product-features">
                                        {product.features.map((feature, index) => (
                                            <li key={index}>
                                                <FaCheck className="feature-icon" />
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                    <Link to={product.link || '/contact'} className="btn btn-primary product-cta">
                                        {product.link ? `View All ${product.title}` : `Send Inquiry for ${product.title}`}
                                        <FaArrowRight />
                                    </Link>
                                </div>
                            );
                        })}
                    </div>
                    )}
                </div>
            </section>

            {/* Info Section */}
            <section className="section section-alt products-info">
                <div className="container">
                    <div className="products-info-content">
                        <div className="info-text">
                            <h2>Why Our Products?</h2>
                            <p>
                                At Medineo Enterprises, we understand that medical institutions require
                                reliable suppliers who can provide genuine products with proper documentation.
                                Every product we supply comes with:
                            </p>
                            <ul>
                                <li><FaCheck /> Authentic sourcing from certified manufacturers</li>
                                <li><FaCheck /> Complete batch documentation and verification</li>
                                <li><FaCheck /> Regulatory compliance certificates</li>
                                <li><FaCheck /> Proper storage and handling during transit</li>
                            </ul>
                        </div>
                        <div className="info-cta">
                            <h3>Looking for specific products?</h3>
                            <p>
                                Contact us with your requirements and our team will assist you
                                with product availability and supply options.
                            </p>
                            <Link to="/contact" className="btn btn-primary btn-lg">
                                Send Inquiry
                                <FaArrowRight />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default Products;
